import type { IssuedPass } from '@/api/passes'
import type { OnlineOrder, PaymentSettings } from '@/api/online-sale'
import { t } from '@/i18n'
import type { Caller } from './abilities'
import { atLeast } from './permissions'

/// Passes bought in the app and paid to the club's own WayForPay merchant —
/// what the panel shows and offers. SPEC: specs/online-sale.md. The server
/// keeps every rule (O1–O11); this only decides which switch is live and
/// what a row says.

// ---- who ----

/// Reading the merchant and the app orders: `view_finance` (finance ≥ view).
export const canSeeOnlinePayment = (caller: Caller): boolean =>
  caller.god || atLeast(caller.profile.permissions.finance, 'view')

/// Changing the merchant: `close_period` (finance = manage) — the people who
/// close the books, not every role that reads a report (§3).
export const canManageOnlinePayment = (caller: Caller): boolean =>
  caller.god || caller.profile.permissions.finance === 'manage'

// ---- the merchant ----

export type MerchantState = 'ready' | 'off' | 'incomplete' | 'unreadable'

/// `ready` is the server's word (on, complete, and the key opens there).
/// On and complete but not ready = the key does not open on this server
/// (a rotated `PASSES_PAYMENTS_KEY`, or none) — the club re-enters it.
export const merchantState = (
  settings: Pick<PaymentSettings, 'enabled' | 'ready' | 'merchantAccount' | 'merchantDomainName' | 'secretKeySet'>,
): MerchantState => {
  if (settings.ready) return 'ready'
  const complete = Boolean(settings.merchantAccount && settings.merchantDomainName && settings.secretKeySet)
  if (!complete) return 'incomplete'
  return settings.enabled ? 'unreadable' : 'off'
}

export type MerchantDraft = Readonly<{ account: string; domain: string; secret: string }>

export const merchantDraftOf = (settings: PaymentSettings | null): MerchantDraft => ({
  account: settings?.merchantAccount ?? '',
  domain: settings?.merchantDomainName ?? '',
  secret: '',
})

/// «https://d-fitness.com.ua/» → «d-fitness.com.ua»: people paste the site's
/// address; WayForPay wants the bare domain.
export const bareDomain = (text: string): string =>
  text
    .trim()
    .replace(/^[a-z]+:\/\//i, '')
    .replace(/\/.*$/, '')
    .toLowerCase()

/// A plausible domain, nothing stricter: a letters TLD (`.ua`, `.укр`) or its
/// punycode (`xn--j1amh`) — WayForPay signs the domain exactly as the shop
/// was registered, and the server keeps the last word.
const DOMAIN = /^(?=.{1,253}$)([a-z0-9Ѐ-ӿ](?:[a-z0-9Ѐ-ӿ-]{0,61}[a-z0-9Ѐ-ӿ])?\.)+(?:[a-zЀ-ӿ]{2,}|xn--[a-z0-9-]+)$/i

/// The server takes 8–200 characters (it trims); shorter is a half-copied key.
export const SECRET_MIN = 8

export type MerchantProblems = Readonly<{ domain?: string; secret?: string }>

export const merchantProblems = (draft: MerchantDraft): MerchantProblems => {
  const domain = bareDomain(draft.domain)
  const secret = draft.secret.trim()
  return {
    ...(domain && !DOMAIN.test(domain) ? { domain: t.sale.payment.domainInvalid } : {}),
    ...(secret && secret.length < SECRET_MIN ? { secret: t.sale.payment.secretShort } : {}),
  }
}

export type MerchantUpdate = Readonly<{
  enabled?: boolean
  merchantAccount?: string | null
  merchantDomainName?: string | null
  merchantSecretKey?: string | null
}>

/// The PUT body: only what changed. An emptied account or domain clears it
/// (null); the key is write-only, so an empty key field means «keep the one
/// you have», never «remove» — removing is its own button (`{ key: null }`).
export const merchantToUpdate = (draft: MerchantDraft, current: PaymentSettings | null): MerchantUpdate => {
  const account = draft.account.trim() || null
  const domain = bareDomain(draft.domain) || null
  const secret = draft.secret.trim()
  return {
    ...(account !== (current?.merchantAccount ?? null) ? { merchantAccount: account } : {}),
    ...(domain !== (current?.merchantDomainName ?? null) ? { merchantDomainName: domain } : {}),
    ...(secret ? { merchantSecretKey: secret } : {}),
  }
}

/// Whether the switch can be turned on: the three things a CREATE_INVOICE
/// needs are stored (O5). Turning it off is always possible.
export const canEnable = (settings: Pick<PaymentSettings, 'merchantAccount' | 'merchantDomainName' | 'secretKeySet'>) =>
  Boolean(settings.merchantAccount && settings.merchantDomainName && settings.secretKeySet)

// ---- a plan on sale in the app ----

export type OnlineToggle = Readonly<{
  /// The switch may be turned ON. Turning it off never needs anything.
  canTurnOn: boolean
  /// Why it cannot (or a note when it can but will not sell yet).
  note: string | null
}>

/// «Продається в застосунку» for a plan at this price: a free plan is never
/// sold online (O1); without a ready merchant there is nothing to invoice
/// with. `settings` null = the caller cannot read finance — the plan can be
/// put on sale, and the note says the club's payment decides when it starts.
export const onlineToggle = (priceMinor: number | null, settings: PaymentSettings | null): OnlineToggle => {
  const p = t.sale.plan
  if (priceMinor === null || priceMinor <= 0) return { canTurnOn: false, note: p.onlineNeedsPrice }
  if (settings === null) return { canTurnOn: true, note: p.onlineCheckPayment }
  if (!canEnable(settings)) return { canTurnOn: false, note: p.onlineNeedsPayment }
  return settings.ready ? { canTurnOn: true, note: null } : { canTurnOn: true, note: p.onlineCheckPayment }
}

// ---- sold passes and orders ----

/// Bought in the app — the issue marks it `paymentMethod: online` (§5).
export const isOnlineSale = (pass: Pick<IssuedPass, 'paymentMethod'>): boolean => pass.paymentMethod === 'online'

export const ORDER_FILTERS = ['attention', 'pending', 'failed', 'all'] as const
export type OrderFilter = (typeof ORDER_FILTERS)[number]

export type OrderQuery = Readonly<{ status?: string; attention?: boolean }>

/// The queries the club's order list sends for a filter (the server's
/// `status` / `attention`, specs/online-sale.md §5 «Club side»). The server
/// stores a lapsed invoice as `pending` and says `expired` only when it
/// answers, so «Не оплачені» asks for `failed` AND `pending` and keeps the
/// lapsed ones (ordersOf) — a «Час вийшов» row is unpaid, not waiting.
export const orderQueriesOf = (filter: OrderFilter): readonly OrderQuery[] =>
  filter === 'attention'
    ? [{ attention: true }]
    : filter === 'all'
      ? [{}]
      : filter === 'pending'
        ? [{ status: 'pending' }]
        : [{ status: 'failed' }, { status: 'pending' }]

/// The answers of orderQueriesOf as one list: by the EFFECTIVE status the
/// filter means, each order once, newest first.
export const ordersOf = <O extends Pick<OnlineOrder, 'id' | 'status' | 'createdAt'>>(
  filter: OrderFilter,
  answers: readonly (readonly O[])[],
): readonly O[] => {
  const keep = (order: O) =>
    filter === 'pending' ? order.status === 'pending' : filter === 'failed' ? order.status === 'failed' || order.status === 'expired' : true
  const unique = [...new Map(answers.flat().map((order) => [order.id, order])).values()]
  return unique.filter(keep).sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

/// Money that may be on the merchant without a pass: paid but not issued,
/// or a callback that could not be checked on an unsettled order.
export const orderNeedsAttention = (order: Pick<OnlineOrder, 'status' | 'callbackProblem'>): boolean =>
  order.status === 'needs_attention' ||
  ((order.status === 'pending' || order.status === 'failed' || order.status === 'expired') && Boolean(order.callbackProblem))

/// Why WayForPay said no, in the club's words: the known `reasonCode`, else
/// the provider's own text; null when it sent neither.
export const providerReasonLine = (
  order: Pick<OnlineOrder, 'providerReasonCode' | 'providerReason'>,
): string | null => {
  const code = order.providerReasonCode
  const known = code == null ? undefined : t.sale.orders.provider[code]
  if (known) return known
  const words = order.providerReason?.trim()
  return words ? (code == null ? words : `${words} (${code})`) : null
}

/// «Не оплачено · невірний CVV-код» — the state and, when there is one, why.
/// A decline names the provider's reason when it sent one.
export const orderStateLine = (
  order: Pick<OnlineOrder, 'status' | 'failureReason' | 'callbackProblem' | 'providerReasonCode' | 'providerReason'>,
): string => {
  const o = t.sale.orders
  const declined = order.failureReason === 'declined' ? providerReasonLine(order) : null
  const why = order.callbackProblem
    ? (o.problem[order.callbackProblem] ?? order.callbackProblem)
    : declined
      ? declined
      : order.failureReason
        ? (o.reason[order.failureReason] ?? order.failureReason)
        : null
  return [o.status[order.status] ?? order.status, why].filter(Boolean).join(' · ')
}
